import { Box, HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { Typography } from 'Tokens';
import { useMemo, useEffect, useState, useRef, useCallback, SetStateAction, Dispatch } from 'react';
import { useParams } from 'react-router-dom';
import { useCurrentCompany } from 'utils/hooks';
import {
  useGetTargetsByStandardAndAssessmentQuery,
  useReportTargetQuestionsQuery,
  useReportActionQuestionsQuery,
  useGetActionsByStandardAndAssessmentQuery,
  useGetAnswersForDrMetricsOnCompanyLevelQuery,
  useGetAnswersForDrMetricsOnGroupLevelQuery,
  useGetTargetsDrQuery,
  useGetMetricAnswersPerDrQuery,
  QuestionType_Enum_,
} from 'models';
import { StructureNode } from '../../Assessment/Structure';
import { DREnums, useGetAggregatedMetricsData } from '../../DisclosureRequirements';
import { filterOptedOutRows } from '../Report.hooks';
import { EsrsReportStandardSwitch } from '../Report.types';
import { ReportActionDR, ReportTargetDR, TargetSummaryTable } from './ReportTargetsDR';
import { E1_6_Target_Metrics, hardcodedMetrics } from './HardcodedTables';
import { FrameworkRefs, Frameworks } from '../../Assessment';
import {
  AggregatedMetricsTableData,
  filterMetricLevels,
  MetricsTableData,
  SelectedMetric,
  TimePeriodsEnums,
  useGetAggregatedMetrics,
  useMaterialStandardId,
} from 'containers/Esrs';
import { Loader, TOP_MENU_HEIGHT } from 'Molecules';
import { GeneratedSummaryStatus } from '../../DisclosureRequirements/Metrics/Metrics';
import { processReportTableData } from '../Report.helpers';
import { uniqBy, orderBy } from 'lodash';
import { getHardcodedMetrics } from '../../DisclosureRequirements/Metrics/DisclosureRequirementPage/DRPageComponents/EditableReport';
import { MetricSidebar } from '../../DisclosureRequirements/Metrics/MetricSidebar';
import { OptOutModal } from '../../DisclosureRequirements/Metrics/OptOutModal';
import { ClickableReportAnswer } from '../../DisclosureRequirements/Metrics/DisclosureRequirementPage/DRPageComponents/ReportClickableAnswers/ClickableReportAnswer';
import { NarrativeReportAnswer } from '../../DisclosureRequirements/Metrics/DisclosureRequirementPage/DRPageComponents/ReportClickableAnswers/NarrativeReportAnswer';
import { StackedMdrmHardcodedTables } from '../../DisclosureRequirements/Metrics/DisclosureRequirementPage/DRPageComponents/StackedMdrmHardcodedTables';
import { DRHardcodedTables } from '../../DisclosureRequirements/Metrics/DisclosureRequirementPage/DRPageComponents/DRHardcodedTables';
import { BackToTopButton } from 'Atoms/BackToTopButton';
import { mfStandardRefs } from 'containers/Esrs/utils';

const hardcodedTableRefs = [...hardcodedMetrics, ...E1_6_Target_Metrics];

const EditableDrCard = ({
  dr,
  standard,
  rowData,
  setRowData,
  urlMetricRef,
}: {
  dr: StructureNode & { type?: string };
  standard: EsrsReportStandardSwitch;
  rowData?: MetricsTableData;
  setRowData: Dispatch<SetStateAction<MetricsTableData | undefined>>;
  urlMetricRef?: string;
}) => {
  const { esrsAssessmentId = '', framework = '' } = useParams();
  const {
    companyAssessmentId: materialStandardId,
    parentAssessmentId: parentStandardId,
    assessmentYear,
  } = useMaterialStandardId(standard.reference ?? '', esrsAssessmentId);
  const { metricDR, metrics, companyLevelReportingUnitId, isGroup, dataLoading } =
    useGetAggregatedMetricsData(
      false,
      materialStandardId,
      parentStandardId,
      false,
      true,
      dr.reference,
      standard.reference
    );

  const reportRef = useRef<HTMLDivElement>(null);
  const company = useCurrentCompany();
  const [selectedMetric, setSelectedMetric] = useState<SelectedMetric | undefined>();
  const [sidebarMetric, setSidebarMetric] = useState<SelectedMetric>();

  const [selectedMdrm, setSelectedMdrm] = useState<string | undefined>();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [generatedSummaryStatus, setGeneratedSummaryStatus] = useState<GeneratedSummaryStatus>();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const handleScroll = useCallback(() => {
    setShowBackToTop((reportRef.current?.scrollTop ?? 0) > 0);
  }, []);

  const isTarget = useMemo(() => dr.type === DREnums.target, [dr]);
  const isAction = useMemo(() => dr.type === DREnums.action, [dr]);
  const customOrDefaultTargetsDR = dr.reference === 'E1-6' ? 'E1-4' : dr.reference; //For hardcoded table
  const shouldGetTargets = dr.reference === 'E1-6' || isTarget;

  const disclosureRequirementRef = dr.reference ?? '';
  const standardRef = standard.reference ?? '';

  const { data: companyMetricAnswersData, loading: loadingCompany } =
    useGetAnswersForDrMetricsOnCompanyLevelQuery({
      variables: { esrsAssessmentId, disclosureRequirementRef, frameworkRef: framework },
      skip: !disclosureRequirementRef || !esrsAssessmentId,
    });
  const { data: groupMetricAnswersData, loading: loadingGroup } =
    useGetAnswersForDrMetricsOnGroupLevelQuery({
      variables: { esrsAssessmentId, disclosureRequirementRef, frameworkRef: framework },
      skip: !isGroup || !disclosureRequirementRef || !esrsAssessmentId,
    });

  const { data: targetsData } = useGetTargetsDrQuery({
    variables: {
      reference: 'E1-4',
      esrsAssessmentId,
      standardRef,
    },
    skip: !esrsAssessmentId || !standardRef || disclosureRequirementRef !== 'E1-6',
  });

  const { data: drTargets, loading: targetsLoading } = useGetTargetsByStandardAndAssessmentQuery({
    variables: {
      esrsAssessmentId,
      standardRef: standard.reference ?? '',
      disclosureRequirementRef: customOrDefaultTargetsDR ?? '',
    },
    skip: !esrsAssessmentId || !standard || !shouldGetTargets,
  });

  const { data: targetQuestions, loading: targetQuestionsLoading } = useReportTargetQuestionsQuery({
    variables: {
      standardRef: standard.reference ?? '',
    },
    skip: !standard || !isTarget,
  });

  const { data: drActions, loading: actionsLoading } = useGetActionsByStandardAndAssessmentQuery({
    variables: {
      esrsAssessmentId,
      standardRef: standard.reference ?? '',
      disclosureRequirementRef: dr.reference ?? '',
    },
    skip: !esrsAssessmentId || !standard || !isAction,
  });

  const { data: actionQuestions, loading: actionQuestionsLoading } = useReportActionQuestionsQuery({
    variables: {
      standardRef: standard.reference ?? '',
    },
    skip: !standard || !isAction,
  });

  const companyAnswersData = useMemo(
    () => companyMetricAnswersData?.EsrsAssessment_by_pk?.reportingUnits ?? [],
    [companyMetricAnswersData]
  );

  const groupAnswersData = useMemo(
    () => groupMetricAnswersData?.EsrsAssessment_by_pk?.subsidiaryAssessments ?? [],
    [groupMetricAnswersData]
  );

  const { allAggregatedMetrics } = useGetAggregatedMetrics(
    metrics ?? [],
    standardRef,
    materialStandardId,
    companyAnswersData,
    groupAnswersData,
    isGroup
  );

  const isEsrs = useMemo(() => framework === FrameworkRefs.esrs, [framework]);

  const processedMetrics = useMemo(() => {
    const uniqueRefs: string[] = [];
    const result = allAggregatedMetrics.map((metric) => processReportTableData(metric, uniqueRefs));
    return result;
  }, [allAggregatedMetrics]);

  const isTargetOrAction = useMemo(
    () => metricDR?.type === 'action' || metricDR?.type === 'target',
    [metricDR]
  );

  const filteredMetrics = useMemo(() => {
    if (metrics) {
      const filteredLevels = isEsrs
        ? filterMetricLevels(metrics, materialStandardId).filter(
            (m) => !hardcodedTableRefs.includes(m.reference)
          )
        : filterMetricLevels(metrics, materialStandardId);
      return uniqBy(
        orderBy(
          filteredLevels,
          [
            (metric) => metric.requirement.frameworkRef !== framework,
            (metric) => metric.globalOrder === null,
            (metric) => metric.globalOrder,
            (metric) => metric.isAR,
            (metric) => metric.order,
            (metric) => metric.firstLevel,
            (metric) => metric.secondLevel,
            (metric) => metric.reference,
          ],
          ['asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc']
        ),
        'reference'
      );
    }
    return [];
  }, [metrics]);

  const [withAssociationMetrics, withoutAssociationMetrics] = useMemo(() => {
    return [
      filteredMetrics.filter((metric) => metric.hasAssociation === true) ?? [],
      filteredMetrics.filter((metric) => metric.hasAssociation === false) ?? [],
    ];
  }, [filteredMetrics]);

  const { data: answersData } = useGetMetricAnswersPerDrQuery({
    variables: {
      reportingUnitId: companyLevelReportingUnitId,
      drRef: disclosureRequirementRef,
      frameworkRef: framework,
    },
  });

  const drTypeName = useMemo(
    () => (metricDR?.type === 'target' ? 'targets' : 'actions'),
    [metricDR]
  );

  const answersMap = useMemo(() => {
    const map = new Map();
    answersData?.esrs_Answer.forEach((ans) => {
      map.set(ans.metricRef, ans.datapoints.find((dp) => !!dp.value)?.value);
    });
    return map;
  }, [answersData]);

  const loading =
    dataLoading ||
    loadingCompany ||
    loadingGroup ||
    targetsLoading ||
    targetQuestionsLoading ||
    actionsLoading ||
    actionQuestionsLoading;

  const hardcodedMetrics = useMemo(
    () => getHardcodedMetrics(disclosureRequirementRef),
    [disclosureRequirementRef]
  );

  const mdrmFilteredMetrics = useMemo(
    () =>
      answersData?.esrs_Answer
        .filter((m) => m.datapoints.find((dp) => dp.isMdrmDisclosed))
        .filter((m) => !m.hasOptedOut)
        .filter(
          (m) =>
            hardcodedMetrics.includes(m.metricRef) ||
            (disclosureRequirementRef === 'E1-6' ? m.metricRef === 'grossScopeGHG2' : '')
        ),
    [answersData, hardcodedMetrics, disclosureRequirementRef]
  );

  const isMFStandard = mfStandardRefs.includes(standardRef);

  useEffect(() => {
    if (!rowData) setSelectedMetric(undefined);
  }, [rowData]);

  useEffect(() => {
    if (metrics.length && urlMetricRef) {
      const element = document.getElementById(urlMetricRef);
      if (element && urlMetricRef === element.id) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'start',
        });
      }
    }
  }, [urlMetricRef, loading, metrics]);

  return (
    <HStack>
      <VStack
        key={metricDR?.reference}
        margin="auto"
        w="100%"
        height="100%"
        alignItems="stretch"
        onScroll={handleScroll}
        css={{
          '&::-webkit-scrollbar': {
            width: '0',
          },
          scrollbarWidth: 'none',
        }}
        ref={reportRef}
      >
        <Box overflowX="visible" w="100%">
          <VStack
            w="100%"
            margin="auto"
            background="bg.elevated.interactive"
            alignItems="start"
            gap="16px"
          >
            {loading ? (
              <Loader height="200px" />
            ) : (
              <>
                {showBackToTop && <BackToTopButton containerRef={reportRef} />}
                <VStack gap="16px" alignItems="start">
                  <Typography variant="h3">
                    {isMFStandard ? '' : metricDR?.reference} {metricDR?.title}
                  </Typography>
                </VStack>
                {isEsrs && !isTargetOrAction && (
                  <>
                    <DRHardcodedTables
                      disclosureRequirementRef={disclosureRequirementRef}
                      aggregatedMetrics={allAggregatedMetrics}
                      answersMap={answersMap}
                      year={assessmentYear}
                      rowData={rowData}
                      selectedMetric={selectedMetric}
                      setSelectedMetric={setSelectedMetric}
                      setRowData={setRowData}
                      targets={targetsData}
                    />
                    {mdrmFilteredMetrics?.map((metric) => {
                      const aggregatedMetric = allAggregatedMetrics.find(
                        (m) => m.metric.reference === metric.metric.reference
                      );

                      return (
                        <StackedMdrmHardcodedTables
                          key={metric.metric.reference}
                          metric={aggregatedMetric?.metric}
                          aggregatedMetric={aggregatedMetric}
                          companyLevelReportingUnitId={companyLevelReportingUnitId}
                          setSelectedMetric={setSelectedMetric}
                          setRowData={setRowData}
                          rowData={rowData}
                          selectedMetric={selectedMetric}
                          selectedMdrm={selectedMdrm}
                          setSelectedMdrm={setSelectedMdrm}
                        />
                      );
                    })}
                  </>
                )}
                {!isTargetOrAction ? (
                  filteredMetrics?.map((m) => {
                    const metric = processedMetrics.find(
                      (met) => met.metric.reference === m.reference
                    );
                    const aggregatedMetric = filterOptedOutRows(
                      metric as AggregatedMetricsTableData,
                      companyAnswersData
                        .find((ans) => ans.isCompanyLevel)
                        ?.answers.filter((ans) => ans.hasOptedOut)
                        .map((ans) => ans.metricRef) ?? []
                    );
                    const hasOptedOut = answersData?.esrs_Answer.find(
                      (ans) => ans.metric.reference === m.reference
                    )?.hasOptedOut;
                    if (!hasOptedOut) {
                      if (m.metricType !== QuestionType_Enum_.Decimal_) {
                        return (
                          <NarrativeReportAnswer
                            key={m.reference}
                            metric={m}
                            answersMap={answersMap}
                            aggregatedMetrics={processedMetrics}
                            textAnswer={answersMap.get(m.reference)}
                            materialStandardId={materialStandardId}
                            companyLevelReportingUnitId={companyLevelReportingUnitId}
                            currency={company.company?.currency}
                            selectedMetric={selectedMetric}
                            setSelectedMetric={setSelectedMetric}
                            rowData={rowData}
                            setRowData={setRowData}
                            drRef={disclosureRequirementRef}
                          />
                        );
                      }
                      return (
                        m.metricType === QuestionType_Enum_.Decimal_ && (
                          <ClickableReportAnswer
                            key={m.reference}
                            metric={m}
                            aggregatedMetric={aggregatedMetric}
                            materialStandardId={materialStandardId}
                            companyLevelReportingUnitId={companyLevelReportingUnitId}
                            currency={company.company?.currency}
                            setSelectedMetric={setSelectedMetric}
                            setRowData={setRowData}
                            rowData={rowData}
                            selectedMetric={selectedMetric}
                            selectedMdrm={selectedMdrm}
                            setSelectedMdrm={setSelectedMdrm}
                            drRef={disclosureRequirementRef}
                          />
                        )
                      );
                    }
                  })
                ) : (
                  <VStack w="100%" alignItems="stretch" gap="24px">
                    {!!withAssociationMetrics.length && (
                      <VStack w="100%" alignItems="start">
                        {isEsrs && (
                          <Typography variant="h3">
                            Disclosures per standard if {drTypeName} are set
                          </Typography>
                        )}
                        {withAssociationMetrics?.map((m) => {
                          const aggregatedMetric = processedMetrics.find(
                            (met) => met.metric.reference === m.reference
                          );
                          if (
                            m.metricType !== QuestionType_Enum_.Decimal_ &&
                            !m.parentMetrics.length
                          ) {
                            return (
                              <NarrativeReportAnswer
                                key={m.reference}
                                metric={m}
                                answersMap={answersMap}
                                aggregatedMetrics={processedMetrics}
                                textAnswer={answersMap.get(m.reference)}
                                materialStandardId={materialStandardId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                selectedMetric={selectedMetric}
                                setSelectedMetric={setSelectedMetric}
                                rowData={rowData}
                                setRowData={setRowData}
                                drRef={disclosureRequirementRef}
                              />
                            );
                          }
                          return (
                            m.metricType === QuestionType_Enum_.Decimal_ && (
                              <ClickableReportAnswer
                                key={m.reference}
                                metric={m}
                                aggregatedMetric={aggregatedMetric}
                                materialStandardId={materialStandardId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                setSelectedMetric={setSelectedMetric}
                                setRowData={setRowData}
                                rowData={rowData}
                                selectedMetric={selectedMetric}
                                selectedMdrm={selectedMdrm}
                                setSelectedMdrm={setSelectedMdrm}
                                drRef={disclosureRequirementRef}
                              />
                            )
                          );
                        })}
                      </VStack>
                    )}
                    {!!withoutAssociationMetrics.length && (
                      <VStack w="100%" alignItems="start">
                        {isEsrs && (
                          <Typography variant="h3">
                            Disclosures per standard if {drTypeName} are not set
                          </Typography>
                        )}
                        {withoutAssociationMetrics?.map((m) => {
                          const aggregatedMetric = processedMetrics.find(
                            (met) => met.metric.reference === m.reference
                          );
                          if (
                            m.metricType !== QuestionType_Enum_.Decimal_ &&
                            !m.parentMetrics.length
                          ) {
                            return (
                              <NarrativeReportAnswer
                                key={m.reference}
                                metric={m}
                                answersMap={answersMap}
                                aggregatedMetrics={processedMetrics}
                                textAnswer={answersMap.get(m.reference)}
                                materialStandardId={materialStandardId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                selectedMetric={selectedMetric}
                                setSelectedMetric={setSelectedMetric}
                                rowData={rowData}
                                setRowData={setRowData}
                                drRef={disclosureRequirementRef}
                              />
                            );
                          }
                          return (
                            m.metricType === QuestionType_Enum_.Decimal_ && (
                              <ClickableReportAnswer
                                key={m.reference}
                                metric={m}
                                aggregatedMetric={aggregatedMetric}
                                materialStandardId={materialStandardId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                setSelectedMetric={setSelectedMetric}
                                setRowData={setRowData}
                                rowData={rowData}
                                selectedMetric={selectedMetric}
                                selectedMdrm={selectedMdrm}
                                setSelectedMdrm={setSelectedMdrm}
                                drRef={disclosureRequirementRef}
                              />
                            )
                          );
                        })}
                      </VStack>
                    )}
                  </VStack>
                )}
              </>
            )}
            {isEsrs &&
              isTarget &&
              drTargets?.targets?.map((target) => (
                <ReportTargetDR
                  key={target.id}
                  target={target}
                  isPreview={true}
                  standard={standard}
                  targetQuestions={targetQuestions}
                  framework={framework}
                />
              ))}
            {isEsrs &&
              isAction &&
              drActions?.actions?.map((action) => (
                <ReportActionDR
                  key={action.id}
                  action={action}
                  isPreview={true}
                  standard={standard}
                  actionQuestions={actionQuestions}
                  framework={framework}
                />
              ))}
            {isTarget && drTargets?.targets.length && (
              <VStack alignItems="start" w="100%">
                <Typography variant="h3" color="text.default">
                  Summary
                </Typography>
                <TargetSummaryTable targets={drTargets?.targets} />
              </VStack>
            )}
          </VStack>
        </Box>
      </VStack>
      {rowData && (
        <Box>
          <OptOutModal
            isOpen={isOpen}
            onClose={onClose}
            assessmentId={esrsAssessmentId}
            reportingUnitId={companyLevelReportingUnitId}
            metric={selectedMetric}
            isCompany={false}
          />
          <Box
            position="fixed"
            top="112px"
            right="0px"
            width="352px"
            height={`calc(100vh - ${TOP_MENU_HEIGHT})`}
            bg="bg.default"
            overflowY="auto"
          >
            <MetricSidebar
              key={rowData?.metric?.reference}
              row={rowData}
              companyCurrency={company.company?.currency ?? ''}
              companyReportingUnit={companyLevelReportingUnitId}
              isGroup={isGroup}
              metric={rowData.metric}
              materialStandardId={materialStandardId}
              selectedQuarter={TimePeriodsEnums.year}
              selectedMetric={sidebarMetric}
              setSelectedMetric={setSidebarMetric}
              onMenuOpen={onOpen}
              setRowData={setRowData}
              isOpenedFromAI
              generatedSummaryStatus={generatedSummaryStatus}
              setGeneratedSummaryStatus={setGeneratedSummaryStatus}
              frameworkName={
                Frameworks[rowData?.metric?.requirement?.frameworkRef as keyof typeof FrameworkRefs]
              }
              reportStandard={standardRef}
            />
          </Box>
        </Box>
      )}
    </HStack>
  );
};

export default EditableDrCard;
